import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: 'website' | 'article' | 'product';
  ogImage?: string;
  noIndex?: boolean;
  structuredData?: object;
}

const BASE_URL = 'https://bosphoruslabs.com';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;
const SITE_NAME = 'Bosphorus Labs';

export function SEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage = DEFAULT_IMAGE,
  noIndex = false,
  structuredData,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Yapay Zeka ve Yazılım Çözümleri`;
  const fullCanonical = canonical ? `${BASE_URL}${canonical}` : BASE_URL;

  const defaultDescription = 'Bosphorus Labs, 8+ yıllık deneyimi ve 9 ülkede 50+ başarılı projesiyle kurumsal yazılım, yapay zeka ve dijital dönüşüm çözümleri sunar.';
  const metaDescription = description || defaultDescription;

  const defaultKeywords = 'yapay zeka, yazılım geliştirme, AI, kurumsal yazılım, dijital dönüşüm, Bosphorus Labs';
  const metaKeywords = keywords || defaultKeywords;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />

      {/* Canonical */}
      <link rel="canonical" href={fullCanonical} />

      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="tr_TR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullCanonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

// Pre-defined SEO configs for each page
export const SEO_CONFIG = {
  home: {
    title: 'Anasayfa',
    description: 'Bosphorus Labs, yapay zeka destekli yazılım çözümleri ve dijital dönüşüm hizmetleri sunan lider teknoloji şirketidir. 8+ yıl deneyim, 9 ülkede 50+ başarılı proje.',
    keywords: 'yapay zeka, yazılım geliştirme, AI, machine learning, kurumsal yazılım, dijital dönüşüm, Bosphorus Labs, Türkiye yazılım şirketi, Ochtopus IDE',
    canonical: '/',
  },
  services: {
    title: 'Hizmetlerimiz',
    description: 'Kurumsal yazılım geliştirme, yapay zeka çözümleri, mobil uygulama, web geliştirme, bulut altyapı ve siber güvenlik hizmetleri. Uzman ekibimizle projenizi hayata geçirin.',
    keywords: 'yazılım geliştirme hizmetleri, kurumsal yazılım, AI danışmanlık, mobil uygulama geliştirme, web geliştirme, bulut çözümleri, siber güvenlik, DevOps',
    canonical: '/services',
  },
  products: {
    title: 'Ürünlerimiz',
    description: 'Ochtopus IDE, Minder, Fulfillment ve daha fazlası. İnovatif yazılım ürünlerimizle işinizi dijitale taşıyın. Yerli ve milli teknoloji çözümleri.',
    keywords: 'Ochtopus IDE, yapay zeka kod editörü, Minder uygulaması, Fulfillment sistemi, e-ticaret entegratör, yerli yazılım ürünleri',
    canonical: '/products',
  },
  about: {
    title: 'Hakkımızda',
    description: 'Bosphorus Labs hikayesi, vizyonumuz ve değerlerimiz. 2016\'dan bu yana teknoloji dünyasında iz bırakıyoruz. Akademisyen düzeyinde uzman ekibimizle tanışın.',
    keywords: 'Bosphorus Labs hakkında, şirket hikayesi, vizyon misyon, teknoloji şirketi, yazılım firması İstanbul, ekip',
    canonical: '/about',
  },
  blog: {
    title: 'Blog',
    description: 'Yapay zeka, yazılım geliştirme, teknoloji trendleri ve sektör analizleri hakkında güncel makaleler. Uzman görüşleri ve teknik içerikler.',
    keywords: 'teknoloji blog, yapay zeka makaleleri, yazılım geliştirme blog, AI trendleri, tech news Türkçe',
    canonical: '/blog',
  },
  careers: {
    title: 'Kariyer',
    description: 'Bosphorus Labs\'da kariyer fırsatları. Yapay zeka, yazılım geliştirme ve teknoloji alanında yetenekli profesyonelleri arıyoruz. Yetenek havuzumuza katılın.',
    keywords: 'yazılım iş ilanları, AI mühendis iş, developer pozisyonları, teknoloji kariyeri, Bosphorus Labs kariyer, İstanbul yazılım işleri',
    canonical: '/careers',
  },
  contact: {
    title: 'İletişim',
    description: 'Bosphorus Labs ile iletişime geçin. Proje teklifleri, iş birlikleri ve sorularınız için bizimle konuşun. 7/24 destek hattı.',
    keywords: 'Bosphorus Labs iletişim, yazılım teklif al, proje danışmanlığı, iş birliği, müşteri hizmetleri',
    canonical: '/contact',
  },
  pricing: {
    title: 'Fiyatlandırma',
    description: 'Yazılım geliştirme ve yapay zeka çözümleri fiyatlandırması. Şeffaf fiyatlar, esnek ödeme seçenekleri. Projenize özel teklif alın.',
    keywords: 'yazılım fiyatları, AI çözüm maliyeti, kurumsal yazılım fiyat, proje teklifi, yazılım bütçe',
    canonical: '/pricing',
  },
  privacy: {
    title: 'Gizlilik Politikası',
    description: 'Bosphorus Labs gizlilik politikası. Kişisel verilerinizin nasıl toplandığı, kullanıldığı ve korunduğu hakkında bilgi.',
    keywords: 'gizlilik politikası, KVKK, kişisel veri koruma, Bosphorus Labs gizlilik',
    canonical: '/privacy',
    noIndex: true,
  },
  terms: {
    title: 'Kullanım Koşulları',
    description: 'Bosphorus Labs web sitesi ve hizmetleri kullanım koşulları ve şartları.',
    keywords: 'kullanım koşulları, hizmet şartları, yasal bilgiler',
    canonical: '/terms',
    noIndex: true,
  },
  security: {
    title: 'Güvenlik',
    description: 'Bosphorus Labs güvenlik politikaları ve uygulamaları. Verileriniz bizimle güvende.',
    keywords: 'güvenlik politikası, veri güvenliği, siber güvenlik, ISO sertifika',
    canonical: '/security',
  },
  download: {
    title: 'İndir',
    description: 'Ochtopus IDE\'yi Windows, macOS ve Linux için indirin. Yapay zeka destekli kod editörü ile üretkenliğinizi artırın.',
    keywords: 'Ochtopus indir, IDE download, yapay zeka kod editörü indir, Windows macOS Linux',
    canonical: '/download',
  },
  enterprise: {
    title: 'Kurumsal Çözümler',
    description: 'Büyük ölçekli kurumsal projeler için özel yazılım çözümleri. Dedicated destek, özel entegrasyonlar ve SLA garantisi.',
    keywords: 'kurumsal yazılım, enterprise çözümler, büyük ölçekli projeler, özel yazılım geliştirme',
    canonical: '/enterprise',
  },
  community: {
    title: 'Topluluk',
    description: 'Bosphorus Labs geliştirici topluluğuna katılın. Discord, GitHub ve sosyal medyada bizi takip edin.',
    keywords: 'geliştirici topluluğu, developer community, discord, github, open source',
    canonical: '/community',
  },
};
